import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { PayrollDashboardPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import ProcessPayrollStatusOverview from 'modules/payroll_bundle/components/PolicyPeriodDashboardContent/RegularCycleSummary/ProcessPayrollStatusOverview'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'

import ExportDataButton from '../../PageHeader/PayrollExportButton'
import UpsellContactButton from '../../PageHeader/UpsellContactButton'
import UpsellPromoModal from '../../PageHeader/UpsellPromoModal'
import { usePayrollBundlePageContext } from '../../PayrollBundle'
import PayrollIssuesPill from '../../PayrollIssuesPill'
import PolicyDropdownSelector from './PolicyDropdownSelector'
import PolicyPeriodDropdownSelector from './PolicyPeriodDropdownSelector'
import SkeletonLoader from './SkeletonLoader'

const RegularCycleSummary = () => {
  const session = useCurrentSession()

  const policies = usePolicies()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    policyId,
    currentPeriod,
    currentPolicyPeriods,
    loadingPolicyPeriodCollection,
    periodIndex,
    setPeriodId,
    setCurrentPolicy,
  } = usePolicyPeriods()
  const selectedPolicyPeriod = currentPolicyPeriods[periodIndex]
  const status = currentPeriod?.attributes.status

  const {
    loading: loadingPayrollContextlessPolicies,
    canDownloadPayrollData,
    canSeeCompanyPayrollResults,
    canUpsellSeeActivationPromo,
    canSeePayrollIssues,
  } = usePayrollContextlessPolicies()
  const [showUpsellModal, setShowUpsellModal] = useState<boolean>(false)

  const { data, loading: loadingPage } = useQuery(PayrollDashboardPageDocument, {
    variables: {
      policyPeriodId: currentPeriod?.id ?? 0,
    },
    skip: !currentPeriod,
  })
  const payrollIssueCount = data?.payroll.payrollIssuesConnection.totalCount

  const { selectedPolicy } = usePayrollBundlePageContext()

  const handlePolicyPeriodChange = (newPolicyPeriodId: number) => {
    setPeriodId(newPolicyPeriodId)
    track(trackingEvents.payroll.userClicksOnChangePolicyPeriodSelector, {
      company_id: session.company.id,
      policy_period_id: newPolicyPeriodId,
    })
  }

  const handlePolicyChange = (newPolicyId: number) => {
    setCurrentPolicy(newPolicyId)

    track(trackingEvents.payroll.userClicksOnChangePolicySelector, {
      company_id: session.company.id,
      policy_id: newPolicyId,
    })

    navigate(navigation.payrollBundle.policy.dashboard.fullPath({ policyId: newPolicyId }))
  }

  const canEditSupplements = data?.payroll._policies.editSupplements?.can ?? false

  const canSeePayrollProcess =
    data?.payroll._policies.seeCompanySepaGenerator?.can ||
    data?.payroll._policies.seeSupplements?.can ||
    canSeeCompanyPayrollResults

  if (loadingPolicyPeriodCollection || loadingPayrollContextlessPolicies || loadingPage) {
    return <SkeletonLoader />
  }

  return (
    <Box gap="s8">
      <Box gap="s24">
        <Box
          width="full"
          gap="s16"
          flexDirection="row"
          justifyContent="flexStart"
          alignItems="center"
          paddingBottom="s6"
        >
          <Box flexDirection="row" justifyContent="spaceBetween" width="full" alignItems="flexEnd">
            <Box flexDirection="row" gap={'s16'}>
              {policies.length > 1 && (
                <PolicyDropdownSelector
                  selectedPolicy={selectedPolicy}
                  policies={policies}
                  onPolicyChange={handlePolicyChange}
                />
              )}

              {selectedPolicyPeriod && (
                <PolicyPeriodDropdownSelector
                  policyPeriods={currentPolicyPeriods}
                  selectedPolicyPeriod={selectedPolicyPeriod}
                  onPolicyPeriodChange={handlePolicyPeriodChange}
                  policy={selectedPolicy}
                />
              )}
            </Box>
            <Box flexDirection="row" gap={'s16'} alignItems="center">
              {policyId && canSeePayrollIssues && !!payrollIssueCount && (
                <PayrollIssuesPill policyId={policyId} issueCount={payrollIssueCount} />
              )}
              {canDownloadPayrollData && <ExportDataButton />}
              {canUpsellSeeActivationPromo && (
                <UpsellContactButton onClick={() => setShowUpsellModal(true)} />
              )}
              {showUpsellModal && <UpsellPromoModal onClose={() => setShowUpsellModal(false)} />}
            </Box>
          </Box>
        </Box>
      </Box>
      {canSeePayrollProcess && (
        <ProcessPayrollStatusOverview
          status={status}
          canEditSupplements={canEditSupplements}
          canSeeCompanyPayrollResults={canSeeCompanyPayrollResults}
        />
      )}
    </Box>
  )
}

export default observer(RegularCycleSummary)
